// src/pages/UserDetails.jsx
import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDistanceToNow } from 'date-fns';
import { ru as dfRu } from 'date-fns/locale/ru';
import { enUS as dfEnUS } from 'date-fns/locale/en-US';
import { ro as dfRo } from 'date-fns/locale/ro';
import { getUserDetails, updateUserStatus, getUserReviews, deleteUserReview, checkAccess, changeBalance, updateCompanyVerified, getCompanyPortfolio, getPurchasedOrders, searchOrders, getUserComments, addUserComment, editUserComment, deleteUserComment, postAdminReview } from '../api';
import StatusBadge from '../components/StatusBadge';
import Icon from '../components/Icon';
import CustomSelect from '../components/CustomSelect';
import AnimatedEmptyState from '../components/AnimatedEmptyState';
import DeferredProfileAvatar from '../components/DeferredProfileAvatar';
import PageSizeDropdown from '../components/PageSizeDropdown';
import ImageLightbox from '../components/ImageLightbox';
import verifiedProviderBadge from '../assets/verified-provider-badge.png';
import '../css/user-details.css';
import '../css/pagination.css';
import { pushDetailNavState } from '../utils/detailNavState';

// Сколько фото портфолио показывать до нажатия «Ещё».
const PHOTOS_PREVIEW_LIMIT = 5;

const UserDetails = () => {
  const { userId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  const [reviews, setReviews] = useState([]);
  const [reviewsTotalCount, setReviewsTotalCount] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [reviewsPage, setReviewsPage] = useState(0);
  const [reviewsPageSize, setReviewsPageSize] = useState(10);
  const [reviewsTotalPages, setReviewsTotalPages] = useState(0);
  const [interactionNote, setInteractionNote] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewHover, setReviewHover] = useState(0);
  const [reviewReviewerId, setReviewReviewerId] = useState('');
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSectionOpen, setReviewSectionOpen] = useState(false);
  const [userComments, setUserComments] = useState([]);
  const [userCommentsLoading, setUserCommentsLoading] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  const [deletingReviewId, setDeletingReviewId] = useState(null);
  const [currentRole, setCurrentRole] = useState(null);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceType, setBalanceType] = useState('ADD');
  const [balanceReason, setBalanceReason] = useState('');
  const [balanceErrors, setBalanceErrors] = useState({});
  const [showBalanceConfirm, setShowBalanceConfirm] = useState(false);
  const [changingBalance, setChangingBalance] = useState(false);
  const [updatingVerification, setUpdatingVerification] = useState(false);
  const [showVerificationConfirm, setShowVerificationConfirm] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [photosExpanded, setPhotosExpanded] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [editFields, setEditFields] = useState({
    fullName: '',
    email: '',
    gender: '',
    preferredLanguage: '',
    plan: '',
    referralCode: ''
  });
  const [isEditing, setIsEditing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [purchasedOrders, setPurchasedOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersPagination, setOrdersPagination] = useState({
    totalPages: 0,
    totalElements: 0
  });
  const [ordersSort, setOrdersSort] = useState('createdAt');
  const [ordersDirection, setOrdersDirection] = useState('DESC');
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersPageSize, setOrdersPageSize] = useState(20);
  const initialReviewsLoadedRef = useRef(null);
  const initialCommentsLoadedRef = useRef(null);

  // === DRAGGABLE BENTO CARDS ===
  const CARD_STORAGE_KEY = `salut_card_order_${userId}`;
  const PIN_STORAGE_KEY = `salut_card_pins_${userId}`;
  const DEFAULT_CARD_ORDER = ['contact', 'personal', 'business', 'system'];
  const PROVIDER_CARD_IDS = ['portfolio', 'work', 'about', 'categories', 'regions', 'services', 'documents', 'photos'];

  const [cardOrder, setCardOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(CARD_STORAGE_KEY);
      return saved ? JSON.parse(saved) : DEFAULT_CARD_ORDER;
    } catch { return DEFAULT_CARD_ORDER; }
  });

  // Объединяем все возможные ID карточек в один массив
  const ALL_CARDS = [...DEFAULT_CARD_ORDER, ...PROVIDER_CARD_IDS];

  const [pinnedCards, setPinnedCards] = useState(() => {
    try {
      const saved = localStorage.getItem(PIN_STORAGE_KEY);
      // Если сохранений нет, возвращаем ALL_CARDS (все залочены) вместо[]
      return saved ? JSON.parse(saved) : ALL_CARDS;
    } catch {
      return ALL_CARDS; // И здесь тоже ALL_CARDS вместо[]
    }
  });

  // Sync missing cards for providers if they have a saved order but missing new blocks
  useEffect(() => {
    if (user?.role === 'PROVIDER' && !loading) {
      setCardOrder(currentOrder => {
        const missing = PROVIDER_CARD_IDS.filter(id => !currentOrder.includes(id));
        if (missing.length > 0) {
          const newOrder = [...currentOrder, ...missing];
          localStorage.setItem(CARD_STORAGE_KEY, JSON.stringify(newOrder));
          return newOrder;
        }
        return currentOrder;
      });
    }
  }, [user?.role, loading, userId]);

  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverCard, setDragOverCard] = useState(null);
  const dragCloneRef = useRef(null);
  const dragPosRef = useRef({ x: 0, y: 0 });
  const dragSmoothRef = useRef({ x: 0, y: 0 });
  const dragRafRef = useRef(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragOverRef = useRef(null);
  const velocityRef = useRef(0);
  const lastXRef = useRef(0);

  const handlePointerDown = (e, cardId) => {
    // 1. Проверки: кнопка пина или залоченная карточка
    if (e.target.closest('.btn-pin-card')) return;
    if (pinnedCards.includes(cardId)) return;
    if (e.button !== 0) return; // Только левый клик

    e.preventDefault();
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();

    // Инициализируем рефы для физики
    lastXRef.current = e.clientX;
    velocityRef.current = 0;
    dragOffsetRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    dragPosRef.current = { x: rect.left, y: rect.top };
    dragSmoothRef.current = { x: rect.left, y: rect.top };

    // 2. Создаем клон
    const clone = card.cloneNode(true);
    clone.style.cssText = `
    position: fixed;
    left: ${rect.left}px;
    top: ${rect.top}px;
    width: ${rect.width}px;
    height: ${rect.height}px;
    z-index: 10000;
    pointer-events: none; /* КРИТИЧНО: чтобы клон не мешал мышке */
    transform: scale(1.03);
    box-shadow: 0 24px 64px rgba(0,0,0,0.18), 0 0 0 2px rgba(59,130,246,0.3);
    border-radius: 16px;
    opacity: 0.95;
    will-change: left, top, transform;
    background: rgba(255,255,255,0.95);
    backdrop-filter: blur(16px);
    transition: transform 0.2s ease; /* Для плавного изменения масштаба */
  `;
    document.body.appendChild(clone);
    dragCloneRef.current = clone;
    setDraggedCard(cardId);

    // 3. Цикл анимации (Физика и движение)
    const animate = () => {
      const cl = dragCloneRef.current;
      if (!cl) return;

      // Инерция позиции (Lerp)
      dragSmoothRef.current.x += (dragPosRef.current.x - dragSmoothRef.current.x) * 0.25;
      dragSmoothRef.current.y += (dragPosRef.current.y - dragSmoothRef.current.y) * 0.25;

      cl.style.left = dragSmoothRef.current.x + 'px';
      cl.style.top = dragSmoothRef.current.y + 'px';

      // Физика наклона
      velocityRef.current *= 0.95; // Затухание
      const tilt = velocityRef.current * 0.9; // Сила наклона

      // Определяем масштаб (уменьшен, если над целью, иначе увеличен)
      const isOverTarget = !!dragOverRef.current;
      const scale = isOverTarget ? 0.98 : 1.03;

      // Применяем трансформацию
      cl.style.transform = `scale(${scale}) rotate(${tilt}deg) rotateY(${tilt * 1.8}deg)`;

      dragRafRef.current = requestAnimationFrame(animate);
    };
    dragRafRef.current = requestAnimationFrame(animate);

    // 4. Обработчик движения
    const onMove = (ev) => {
      // Считаем скорость для наклона
      const deltaX = ev.clientX - lastXRef.current;
      velocityRef.current = deltaX;
      lastXRef.current = ev.clientX;

      // Обновляем целевую позицию
      dragPosRef.current = {
        x: ev.clientX - dragOffsetRef.current.x,
        y: ev.clientY - dragOffsetRef.current.y
      };

      // Ищем, над какой карточкой мы сейчас
      const els = document.elementsFromPoint(ev.clientX, ev.clientY);
      const overCard = els.find(el => el.dataset && el.dataset.cardid && el.dataset.cardid !== cardId);

      if (overCard && !pinnedCards.includes(overCard.dataset.cardid)) {
        const targetId = overCard.dataset.cardid;
        if (dragOverRef.current !== targetId) {
          setDragOverCard(targetId);
          dragOverRef.current = targetId;
        }
      } else {
        if (dragOverRef.current !== null) {
          setDragOverCard(null);
          dragOverRef.current = null;
        }
      }
    };

    // 5. Обработчик отпускания (Drop)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      cancelAnimationFrame(dragRafRef.current);

      const targetId = dragOverRef.current;
      let destRect = null;

      if (targetId && targetId !== cardId) {
        const targetEl = document.querySelector(`[data-cardid="${targetId}"]`);
        if (targetEl) destRect = targetEl.getBoundingClientRect();

        setCardOrder(order => {
          const newOrder = [...order];
          const fromIdx = newOrder.indexOf(cardId);
          const toIdx = newOrder.indexOf(targetId);
          if (fromIdx !== -1 && toIdx !== -1) {
            newOrder[fromIdx] = newOrder[toIdx];
            newOrder[toIdx] = cardId;
            localStorage.setItem(CARD_STORAGE_KEY, JSON.stringify(newOrder));
          }
          return newOrder;
        });
      } else {
        const origEl = document.querySelector(`[data-cardid="${cardId}"]`);
        if (origEl) destRect = origEl.getBoundingClientRect();
      }

      setDragOverCard(null);
      dragOverRef.current = null;

      const cl = dragCloneRef.current;
      if (cl) {
        cl.style.transition = 'all 0.2s cubic-bezier(0.2, 0, 0, 1)';
        if (destRect) {
          cl.style.left = destRect.left + 'px';
          cl.style.top = destRect.top + 'px';
        }
        cl.style.transform = 'scale(1) rotate(0deg) rotateY(0deg)';

        setTimeout(() => {
          if (cl.parentNode) {
            cl.style.opacity = '0';
            setTimeout(() => cl.parentNode?.removeChild(cl), 20);
          }
          setDraggedCard(null);
        }, 180);
      } else {
        setDraggedCard(null);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const togglePinCard = (cardId) => {
    const newPins = pinnedCards.includes(cardId)
      ? pinnedCards.filter(id => id !== cardId)
      : [...pinnedCards, cardId];
    setPinnedCards(newPins);
    localStorage.setItem(PIN_STORAGE_KEY, JSON.stringify(newPins));
  };

  const locale = useMemo(() => {
    if (i18n.language === 'ro') return 'ro-RO';
    if (i18n.language === 'ru') return 'ru-RU';
    return 'en-US';
  }, [i18n.language]);

  const dateFnsLocale = useMemo(() => {
    if (i18n.language === 'ro') return dfRo;
    if (i18n.language === 'ru') return dfRu;
    return dfEnUS;
  }, [i18n.language]);

  const debtRelativePhrase = useCallback(
    (iso) => {
      if (!iso) return null;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return null;
      const sec = (Date.now() - d.getTime()) / 1000;
      if (sec < 60) return t('userDetails.balance.debtJustNow');
      return formatDistanceToNow(d, { addSuffix: true, locale: dateFnsLocale });
    },
    [dateFnsLocale, t]
  );

  const debtDurationLabel = useMemo(() => {
    if (!user?.debtStartedAt) return null;
    return debtRelativePhrase(user.debtStartedAt);
  }, [user?.debtStartedAt, debtRelativePhrase]);

  const fromComplaint = location.state?.fromComplaint;
  const fromOrder = location.state?.fromOrder;
  const fromUser = location.state?.fromUser;
  const fromLogs = location.state?.fromLogs;

  const planLabels = useMemo(() => t('users.plans', { returnObjects: true }), [t]);
  const roleLabels = useMemo(() => t('users.roles', { returnObjects: true }), [t]);
  const statusLabels = useMemo(() => t('users.statuses', { returnObjects: true }), [t]);

  const userStatuses = useMemo(() => ([
    { value: 'ACTIVE', label: statusLabels.ACTIVE || 'ACTIVE', color: 'green' },
    { value: 'INACTIVE', label: statusLabels.INACTIVE || 'INACTIVE', color: 'yellow' },
    { value: 'BLOCKED', label: statusLabels.BLOCKED || 'BLOCKED', color: 'red' }
  ]), [statusLabels]);

  const canDeleteReviews = currentRole === 'MODERATOR' || currentRole === 'ADMIN' || currentRole === 'DEV';

  // Helper function to get status label for display in messages
  const getStatusLabel = (status) => {
    const statusObj = userStatuses.find(s => s.value === status);
    return statusObj ? statusObj.label : status;
  };

  useEffect(() => {
    const loadRole = async () => {
      try {
        const role = await checkAccess();
        setCurrentRole(role);
      } catch (err) {
        console.error('Ошибка проверки доступа:', err);
      }
    };

    loadRole();
  }, []);

  const loadCompanyData = useCallback(async (userProfileId) => {
    setCompanyLoading(true);
    try {
      const data = await getCompanyPortfolio(userProfileId);
      // Тяжёлая сетка с фото — не блокируем первый кадр с профилем
      startTransition(() => {
        setCompanyData(data);
      });
    } catch (err) {
      console.error('Ошибка загрузки данных компании:', err);
    } finally {
      setCompanyLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchUserDetails = async () => {
      setLoading(true);
      setActiveTab('profile');
      setPendingStatus(null);
      setMessage(null);
      setInteractionNote('');
      setReviewRating(5);
      setReviewHover(0);
      setReviewReviewerId('');
      setReviewComment('');
      setSubmittingReview(false);
      setReviewSectionOpen(false);
      setUserComments([]);
      setUserCommentsLoading(false);
      setDeletingReviewId(null);
      setReviews([]);
      setReviewsTotalCount(0);
      setReviewsPage(0);
      setReviewsTotalPages(0);
      initialReviewsLoadedRef.current = null;
      setCompanyData(null);

      try {
        const data = await getUserDetails(userId);
        setUser(data);
        setReviewsTotalCount(Number(data?.countRating) || 0);
        // Портфолио/фото загружаем отдельным эффектом после первой отрисовки (см. useEffect ниже)
      } catch (err) {
        console.error('Ошибка загрузки деталей пользователя:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUserDetails();
  }, [userId]);

  // Портфолио и фото — после первого paint, чтобы не «рвало» открытие карточки пользователя
  useEffect(() => {
    if (!user || user.role !== 'PROVIDER' || !user.id) return undefined;
    if (String(user.id) !== String(userId)) return undefined;
    let cancelled = false;
    const run = () => {
      if (!cancelled) loadCompanyData(user.id);
    };
    const idle =
      typeof window !== 'undefined' && 'requestIdleCallback' in window
        ? window.requestIdleCallback(run, { timeout: 600 })
        : setTimeout(run, 80);
    return () => {
      cancelled = true;
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window && typeof idle === 'number') {
        window.cancelIdleCallback(idle);
      } else {
        clearTimeout(idle);
      }
    };
  }, [user?.id, user?.role, userId, loadCompanyData]);

  const normalizeReview = useCallback((review) => {
    const info = review?.reviewerInfo || {};
    return {
      ...review,
      reviewerAvatar: review?.reviewerAvatar ?? info.reviewerAvatar ?? info.avatar ?? null,
      reviewerFullName: review?.reviewerFullName ?? info.reviewerFullName ?? info.fullName ?? null,
      reviewerRating: review?.reviewerRating ?? info.reviewerRating ?? info.rating ?? null,
      reviewerPlan: review?.reviewerPlan ?? info.reviewerPlan ?? info.plan ?? null,
      reviewerId: review?.reviewerId ?? info.reviewerId ?? info.id ?? null
    };
  }, []);

  const loadReviews = useCallback(async (page = reviewsPage, size = reviewsPageSize) => {
    setReviewsLoading(true);
    try {
      const data = await getUserReviews(userId, page, size);
      const normalized = (data.content || []).map(normalizeReview);
      setReviews(normalized);
      setReviewsTotalCount(Number(data?.totalElements) || normalized.length || 0);
      setReviewsTotalPages(Number(data?.totalPages) || 0);
      setReviewsPage(page);
    } catch (err) {
      console.error('Ошибка загрузки отзывов:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.reviews.loadError')
      });
    } finally {
      setReviewsLoading(false);
    }
  }, [userId, t, normalizeReview, reviewsPage, reviewsPageSize]);

  const handleReviewsPageChange = useCallback((page) => {
    loadReviews(page, reviewsPageSize);
  }, [loadReviews, reviewsPageSize]);

  const handleReviewsPageSizeChange = useCallback((newSize) => {
    const size = parseInt(newSize, 10) || 10;
    setReviewsPageSize(size);
    loadReviews(0, size);
  }, [loadReviews]);

  const getReviewsVisiblePages = () => {
    const totalPages = reviewsTotalPages || 0;
    const current = reviewsPage;
    if (totalPages <= 1) return [];
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i);

    const pages = [0];
    const left = Math.max(1, current - 1);
    const right = Math.min(totalPages - 2, current + 1);
    if (left > 1) pages.push('left-ellipsis');
    for (let p = left; p <= right; p += 1) pages.push(p);
    if (right < totalPages - 2) pages.push('right-ellipsis');
    pages.push(totalPages - 1);
    return pages;
  };

  const renderReviewsPager = (position = 'bottom') => {
    if ((reviewsLoading && reviews.length === 0) || reviewsTotalPages <= 0) return null;
    const isFirst = reviewsPage <= 0;
    const isLast = reviewsPage >= reviewsTotalPages - 1;
    const pages = getReviewsVisiblePages();

    return (
      <div className={`orders-pager orders-pager-${position}`}>
        <div className="orders-pager-info">
          {t('common.pagination.showing', {
            from: reviewsTotalCount > 0 ? reviewsPage * reviewsPageSize + 1 : 0,
            to: Math.min((reviewsPage + 1) * reviewsPageSize, reviewsTotalCount),
            total: reviewsTotalCount
          })}
        </div>

        <div className="orders-pager-controls">
          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handleReviewsPageChange(reviewsPage - 1)}
            disabled={isFirst || reviewsLoading}
          >
            {t('common.pagination.previous')}
          </button>

          <div className="orders-pager-pages">
            {pages.map((item) => {
              if (typeof item !== 'number') {
                return (
                  <span key={item} className="orders-pager-ellipsis">
                    ...
                  </span>
                );
              }
              const active = item === reviewsPage;
              return (
                <button
                  key={item}
                  type="button"
                  className={`orders-pager-page ${active ? 'active' : ''}`}
                  onClick={() => handleReviewsPageChange(item)}
                  disabled={reviewsLoading}
                  aria-current={active ? 'page' : undefined}
                >
                  {item + 1}
                </button>
              );
            })}
          </div>

          <button
            type="button"
            className="orders-pager-nav"
            onClick={() => handleReviewsPageChange(reviewsPage + 1)}
            disabled={isLast || reviewsLoading}
          >
            {t('common.pagination.next')}
          </button>

          <PageSizeDropdown
            value={reviewsPageSize}
            onChange={handleReviewsPageSizeChange}
            options={[10, 20, 50, 100]}
            position={position}
          />
        </div>
      </div>
    );
  };

  const handleDeleteReview = async (reviewId) => {
    if (!reviewId || deletingReviewId) return;
    const confirmed = window.confirm(t('userDetails.reviews.deleteConfirm'));
    if (!confirmed) return;

    try {
      setDeletingReviewId(reviewId);
      setMessage(null);
      await deleteUserReview(reviewId);
      setReviews((prev) => prev.filter((review) => review.id !== reviewId));
      setReviewsTotalCount((prev) => (prev > 0 ? prev - 1 : 0));
      setMessage({
        type: 'success',
        text: t('userDetails.reviews.deleteSuccess')
      });
    } catch (err) {
      console.error('Ошибка удаления отзыва:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.reviews.deleteError')
      });
    } finally {
      setDeletingReviewId(null);
    }
  };

  useEffect(() => {
    if (activeTab !== 'reviews') return;
    if (!userId) return;
    if (initialReviewsLoadedRef.current === userId) return;
    initialReviewsLoadedRef.current = userId;
    loadReviews();
  }, [activeTab, userId, loadReviews]);

  const loadPurchasedOrders = useCallback(async () => {
    if (!user || !user.id) return;
    if (user.role !== 'PROVIDER' && user.role !== 'CUSTOMER') return;

    setOrdersLoading(true);
    try {
      const data = user.role === 'PROVIDER'
        ? await getPurchasedOrders(user.id, ordersPage, ordersPageSize, ordersSort, ordersDirection)
        : await searchOrders({ userId: user.id, page: ordersPage, size: ordersPageSize, sort: ordersSort, direction: ordersDirection });
      setPurchasedOrders(data.content || []);
      setOrdersPagination({
        totalPages: data.totalPages || 0,
        totalElements: data.totalElements || 0
      });
    } catch (err) {
      console.error('Ошибка загрузки заказов:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.orders.loadError')
      });
    } finally {
      setOrdersLoading(false);
    }
  }, [user, ordersPage, ordersPageSize, ordersSort, ordersDirection, t]);

  useEffect(() => {
    if (activeTab === 'orders' && user && (user.role === 'PROVIDER' || user.role === 'CUSTOMER')) {
      loadPurchasedOrders();
    }
  }, [activeTab, user, loadPurchasedOrders]);

  // Заранее подтягиваем количество заказов клиента для счётчика на вкладке (до её открытия)
  useEffect(() => {
    if (!user || user.id == null || user.role !== 'CUSTOMER') return undefined;
    if (String(user.id) !== String(userId)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await searchOrders({ userId: user.id, page: 0, size: 1 });
        if (!cancelled) {
          setOrdersPagination(prev => ({ ...prev, totalElements: data.totalElements || 0 }));
        }
      } catch (err) {
        console.error('Ошибка загрузки количества заказов:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id, user?.role, userId]);

  const handleStatusChange = async (newStatus) => {
    if (!user || updating) return;

    setEditFields({
      fullName: user.fullName || '',
      email: user.email || '',
      gender: user.gender || '',
      preferredLanguage: user.preferredLanguage || '',
      plan: user.plan || '',
      referralCode: user.referralCode || ''
    });
    setIsEditing(true);
    setPendingStatus(newStatus);
  };

  const handleOpenEditModal = () => {
    if (!user || updating) return;

    setEditFields({
      fullName: user.fullName || '',
      email: user.email || '',
      gender: user.gender || '',
      preferredLanguage: user.preferredLanguage || '',
      plan: user.plan || '',
      referralCode: user.referralCode || ''
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditFields({
      fullName: '',
      email: '',
      gender: '',
      preferredLanguage: '',
      plan: '',
      referralCode: ''
    });
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;

    try {
      setUpdating(true);
      setMessage(null);

      const updateData = {
        status: pendingStatus,
        fullName: editFields.fullName?.trim() || undefined,
        email: editFields.email?.trim() || undefined,
        gender: editFields.gender || undefined,
        preferredLanguage: editFields.preferredLanguage || undefined,
        plan: editFields.plan || undefined,
        referralCode: editFields.referralCode?.trim() || undefined
      };

      const updatedUser = await updateUserStatus(userId, updateData, user);
      setUser(updatedUser);

      setMessage({
        type: 'success',
        text: t('userDetails.messages.userUpdated')
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Ошибка изменения статуса:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.messages.statusUpdateError', { error: err.message })
      });
    } finally {
      setUpdating(false);
      setPendingStatus(null);
      setIsEditing(false);
    }
  };

  const handleSaveUserEdit = async () => {
    if (!editFields.fullName.trim()) return;

    try {
      setUpdating(true);
      setMessage(null);

      const updateData = {
        status: user.status,
        fullName: editFields.fullName?.trim() || undefined,
        email: editFields.email?.trim() || undefined,
        gender: editFields.gender || undefined,
        preferredLanguage: editFields.preferredLanguage || undefined,
        plan: editFields.plan || undefined,
        referralCode: editFields.referralCode?.trim() || undefined
      };

      const updatedUser = await updateUserStatus(userId, updateData, user);
      setUser(updatedUser);

      setMessage({
        type: 'success',
        text: t('userDetails.messages.userUpdated')
      });

      handleCloseEditModal();
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Ошибка обновления пользователя:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.messages.userUpdateError', { error: err.message })
      });
    } finally {
      setUpdating(false);
    }
  };

  const cancelStatusChange = () => {
    setPendingStatus(null);
    setIsEditing(false);
    setEditFields({
      fullName: '',
      email: '',
      gender: '',
      preferredLanguage: '',
      plan: '',
      referralCode: ''
    });
  };

  const getStatusChangeText = () => {
    if (!pendingStatus || !user) return { action: t('userDetails.statusActions.change'), question: t('userDetails.statusActions.changeTitle') };

    const currentStatus = user.status;
    const newStatus = pendingStatus;

    if (newStatus === 'BLOCKED') {
      return {
        action: t('userDetails.statusActions.blockAction'),
        question: t('userDetails.statusActions.blockQuestion', { name: user.fullName })
      };
    }

    if (currentStatus === 'BLOCKED' && newStatus !== 'BLOCKED') {
      return {
        action: t('userDetails.statusActions.unblockAction'),
        question: t('userDetails.statusActions.unblockQuestion', { name: user.fullName })
      };
    }

    if (newStatus === 'INACTIVE') {
      return {
        action: t('userDetails.statusActions.deactivateAction'),
        question: t('userDetails.statusActions.statusChangeQuestion', { name: user.fullName, status: getStatusLabel(newStatus) })
      };
    }

    return {
      action: t('userDetails.statusActions.change'),
      question: t('userDetails.statusActions.statusChangeQuestion', { name: user.fullName, status: getStatusLabel(newStatus) })
    };
  };

  const handleOpenReviewer = (reviewerId) => {
    if (!reviewerId) return;
    navigate(`/users/${reviewerId}`, {
      state: pushDetailNavState(location, {
        fromUser: location.pathname,
        fromUsers: location.state?.fromUsers,
        fromPromo: location.state?.fromPromo,
      }),
    });
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({
        type: 'success',
        text: t('userDetails.messages.copied')
      });
      setTimeout(() => setMessage(null), 2000);
    });
  };

  /** Данные текущего саппорта из localStorage (после логина) — API при POST комментария часто не возвращает ФИО/роль */
  const getStoredAuthUser = () => {
    try {
      const raw = localStorage.getItem('user');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const enrichCommentFromSession = (raw) => {
    if (!raw) return raw;
    const auth = getStoredAuthUser();
    const name =
      raw.authorFullName ||
      raw.authorName ||
      raw.createdByName ||
      auth?.fullName ||
      auth?.username ||
      '';
    const role = raw.authorRole || auth?.role || currentRole || undefined;
    return { ...raw, authorFullName: name || raw.authorFullName, authorRole: role };
  };

  const loadUserComments = useCallback(async () => {
    if (!userId) return;
    setUserCommentsLoading(true);
    try {
      const comments = await getUserComments(userId);
      setUserComments((comments || []).map(enrichCommentFromSession));
    } catch (err) {
      console.error('Ошибка загрузки заметок:', err);
      setMessage({
        type: 'error',
        text: err.message || t('common.error')
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setUserCommentsLoading(false);
    }
  }, [userId, t, enrichCommentFromSession]);

  useEffect(() => {
    if (!userId) return;
    if (initialCommentsLoadedRef.current === userId) return;
    initialCommentsLoadedRef.current = userId;
    loadUserComments();
  }, [userId, loadUserComments]);

  const renderStars = (rating) => {
    const stars = Math.round(rating || 0);
    return (
      <span className="stars-container" style={{ display: 'inline-flex', gap: '2px', color: '#f59e0b' }}>
        {[...Array(5)].map((_, i) => (
          <Icon key={i} name={i < stars ? 'star-fill' : 'star'} size={14} />
        ))}
      </span>
    );
  };

  const handleSaveNote = async () => {
    if (!interactionNote.trim()) return;

    try {
      const comment = await addUserComment(userId, interactionNote.trim());
      const enriched = enrichCommentFromSession(comment);
      setUserComments((prev) => [enriched, ...(prev || [])]);
      setMessage({
        type: 'success',
        text: t('userDetails.messages.noteSaved')
      });
      setInteractionNote('');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Ошибка сохранения заметки:', err);
      setMessage({
        type: 'error',
        text: err.message || t('common.error')
      });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleSubmitReview = async () => {
    if (submittingReview) return;

    const reviewerId = reviewReviewerId.trim();
    const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidPattern.test(reviewerId)) {
      setMessage({
        type: 'error',
        text: t('userDetails.interaction.review.invalidReviewer', { defaultValue: 'Укажите корректный ID аккаунта (UUID)' })
      });
      setTimeout(() => setMessage(null), 4000);
      return;
    }

    try {
      setSubmittingReview(true);
      setMessage(null);
      await postAdminReview({
        reviewerId,
        reviewedProfileId: userId,
        rating: reviewRating,
        comment: reviewComment
      });
      setMessage({
        type: 'success',
        text: t('userDetails.messages.reviewSaved', { defaultValue: 'Отзыв опубликован' })
      });
      setReviewRating(5);
      setReviewHover(0);
      setReviewReviewerId('');
      setReviewComment('');
      // Обновляем список отзывов, чтобы новый сразу отобразился
      initialReviewsLoadedRef.current = userId;
      loadReviews(0, reviewsPageSize);
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Ошибка публикации отзыва:', err);
      setMessage({
        type: 'error',
        text: err.message || t('common.error')
      });
      setTimeout(() => setMessage(null), 4000);
    } finally {
      setSubmittingReview(false);
    }
  };

  const startEditComment = (comment) => {
    const id = comment.id || comment.commentId;
    if (!id) return;
    setEditingCommentId(id);
    setEditingCommentText(comment.text || comment.comment || '');
  };

  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditingCommentText('');
  };

  const handleUpdateComment = async () => {
    if (!editingCommentId || !editingCommentText.trim()) return;
    try {
      const updated = await editUserComment(editingCommentId, editingCommentText.trim());
      const merged = enrichCommentFromSession({ ...updated });
      setUserComments((prev) =>
        (prev || []).map((c) => {
          const cid = c.id || c.commentId;
          return cid === editingCommentId ? merged : c;
        })
      );
      setMessage({
        type: 'success',
        text: t('userDetails.messages.noteSaved')
      });
      setEditingCommentId(null);
      setEditingCommentText('');
      setTimeout(() => setMessage(null), 3000);
    } catch (err) {
      console.error('Ошибка редактирования заметки:', err);
      setMessage({
        type: 'error',
        text: err.message || t('common.error')
      });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleDeleteComment = async (comment) => {
    const id = comment.id || comment.commentId;
    if (!id) return;
    if (!window.confirm(t('userDetails.interaction.deleteConfirm'))) {
      return;
    }
    try {
      await deleteUserComment(id);
      setUserComments((prev) => (prev || []).filter((c) => (c.id || c.commentId) !== id));
      if (editingCommentId === id) {
        setEditingCommentId(null);
        setEditingCommentText('');
      }
    } catch (err) {
      console.error('Ошибка удаления заметки:', err);
      setMessage({
        type: 'error',
        text: err.message || t('common.error')
      });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const handleOpenBalanceModal = () => {
    setShowBalanceModal(true);
    setBalanceAmount('');
    setBalanceType('ADD');
    setBalanceReason('');
    setBalanceErrors({});
    setShowBalanceConfirm(false);
  };

  const handleCloseBalanceModal = () => {
    setShowBalanceModal(false);
    setBalanceAmount('');
    setBalanceType('ADD');
    setBalanceReason('');
    setBalanceErrors({});
    setShowBalanceConfirm(false);
  };

  const validateBalanceForm = () => {
    const errors = {};
    const MAX_BALANCE = 99999999.99;
    const amount = parseFloat(balanceAmount);

    if (!balanceAmount || isNaN(amount) || amount < 1) {
      errors.amount = t('userDetails.balance.errors.amountInvalid');
    } else if (amount > MAX_BALANCE) {
      errors.amount = t('userDetails.balance.errors.amountTooLarge', {
        max: MAX_BALANCE.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      });
    } else {
      const currentBalance = parseFloat(user.balance || 0);
      let newBalance;

      if (balanceType === 'ADD') {
        newBalance = currentBalance + amount;
      } else {
        newBalance = currentBalance - amount;
      }

      if (newBalance > MAX_BALANCE) {
        errors.amount = t('userDetails.balance.errors.balanceExceedsMax', {
          max: MAX_BALANCE.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        });
      }
    }

    if (!balanceReason || !balanceReason.trim()) {
      errors.reason = t('userDetails.balance.errors.reasonRequired');
    }

    setBalanceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBalanceSubmit = () => {
    if (!validateBalanceForm()) {
      return;
    }
    setShowBalanceConfirm(true);
  };

  const handleConfirmBalanceChange = async () => {
    if (!validateBalanceForm()) {
      setShowBalanceConfirm(false);
      return;
    }

    try {
      setChangingBalance(true);
      setMessage(null);

      await changeBalance(
        user.id || userId,
        parseFloat(balanceAmount),
        balanceType,
        balanceReason.trim()
      );

      const updatedUser = await getUserDetails(userId);
      setUser(updatedUser);

      setMessage({
        type: 'success',
        text: t('userDetails.balance.success')
      });

      handleCloseBalanceModal();
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Ошибка изменения баланса:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.balance.error', { error: err.message })
      });
    } finally {
      setChangingBalance(false);
    }
  };

  const handleToggleVerification = async () => {
    if (!user || user.role !== 'PROVIDER' || updatingVerification) return;

    const nextVerified = !Boolean(user.verified);
    let updated = false;
    try {
      setUpdatingVerification(true);
      setMessage(null);
      await updateCompanyVerified(user.id, nextVerified);
      setUser((prev) => (prev ? { ...prev, verified: nextVerified } : prev));
      setMessage({
        type: 'success',
        text: nextVerified
          ? t('userDetails.verification.successVerified')
          : t('userDetails.verification.successUnverified'),
      });
      updated = true;
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      console.error('Ошибка изменения верификации компании:', err);
      setMessage({
        type: 'error',
        text: t('userDetails.verification.error', { error: err.message }),
      });
    } finally {
      setUpdatingVerification(false);
    }
    return updated;
  };

  const handleOpenVerificationConfirm = () => {
    if (!user || user.role !== 'PROVIDER' || updatingVerification) return;
    setShowVerificationConfirm(true);
  };

  const handleCloseVerificationConfirm = () => {
    if (updatingVerification) return;
    setShowVerificationConfirm(false);
  };

  if (loading) {
    return (
      <div className="user-details-page">
        <div className="loading-state">
          <div className="strict-loader"></div>
          <div>{t('userDetails.loading')}</div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-details-page">
        <div className="error-state">
          <div className="warning-text">{t('userDetails.notFound')}</div>
        </div>
      </div>
    );
  }

  const renderInPortal = (node) =>
    (typeof document !== 'undefined' ? createPortal(node, document.body) : null);
  const openLightbox = (images, index = 0) => {
    if (!images?.length) return;
    setLightbox({ images, index });
  };
  const avatarStatusClass = `status-${String(user.status || 'INACTIVE').toLowerCase()}`;
  const interactionCommentsCount = userComments?.length || 0;
  const interactionTabLabel = interactionCommentsCount > 0
    ? `${t('userDetails.tabs.interaction')} (${interactionCommentsCount})`
    : t('userDetails.tabs.interaction');

  return (
    <div className="user-details-page">
      {message && (
        <div className={`message-toast ${message.type}`}>
          {message.text}
          <button
            className="message-close"
            onClick={() => setMessage(null)}
          >
            ×
          </button>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {pendingStatus && (() => {
        const statusText = getStatusChangeText();
        return renderInPortal(
          <div className="modal-overlay user-edit-overlay user-details-modal-overlay">
            <div className="modal-content user-edit-modal user-details-modal-content">
              <div className="modal-header">
                <div className="user-edit-modal-title-row">
                  <span className="user-edit-modal-icon" aria-hidden="true">
                    <Icon name="edit-user" size={18} />
                  </span>
                  <h3>{t('userDetails.modal.editUserTitle')}</h3>
                </div>
              </div>
              <div className="modal-body">
                <div className="edit-user-form">
                  <div className="form-group full-width">
                    <label className="form-label">
                      {t('userDetails.modal.status')}
                    </label>
                    <div className="status-preview">
                      {statusText.question}
                    </div>
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">
                      {t('userDetails.labels.fullName')}
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFields.fullName}
                      onChange={(e) => setEditFields({ ...editFields, fullName: e.target.value })}
                      placeholder={t('userDetails.modal.fullNamePlaceholder')}
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">
                      {t('userDetails.labels.email')}
                    </label>
                    <input
                      type="email"
                      className="form-input"
                      value={editFields.email}
                      onChange={(e) => setEditFields({ ...editFields, email: e.target.value })}
                      placeholder={t('userDetails.modal.emailPlaceholder')}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('userDetails.labels.gender')}
                    </label>
                    <CustomSelect
                      value={editFields.gender}
                      onChange={(val) => setEditFields({ ...editFields, gender: val })}
                      placeholder={t('userDetails.modal.selectGender')}
                      options={[
                        { value: 'MALE', label: t('userDetails.modal.male') },
                        { value: 'FEMALE', label: t('userDetails.modal.female') }
                      ]}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('userDetails.labels.language')}
                    </label>
                    <CustomSelect
                      value={editFields.preferredLanguage}
                      onChange={(val) => setEditFields({ ...editFields, preferredLanguage: val })}
                      placeholder={t('userDetails.modal.selectLanguage')}
                      options={[
                        { value: 'ro', label: 'Română' },
                        { value: 'ru', label: 'Русский' },
                        { value: 'en', label: 'English' }
                      ]}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('userDetails.modal.plan')}
                    </label>
                    <CustomSelect
                      value={editFields.plan}
                      onChange={(val) => setEditFields({ ...editFields, plan: val })}
                      placeholder={t('userDetails.modal.selectPlan')}
                      options={[
                        { value: 'BASIC', label: planLabels?.BASIC || 'BASIC' },
                        { value: 'PRO', label: planLabels?.PRO || 'PRO' }
                      ]}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      {t('userDetails.labels.referralCode')}
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={editFields.referralCode}
                      onChange={(e) => setEditFields({ ...editFields, referralCode: e.target.value })}
                      placeholder={t('userDetails.modal.referralCodePlaceholder')}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  className="btn btn-secondary"
                  onClick={cancelStatusChange}
                  disabled={updating}
                >
                  {t('userDetails.modal.cancel')}
                </button>
                <button
                  className={`btn ${pendingStatus === 'BLOCKED' ? 'btn-danger' : 'btn-primary'}`}
                  onClick={confirmStatusChange}
                  disabled={updating || !editFields.fullName.trim()}
                >
                  {updating ? t('common.loading') : t('userDetails.modal.save')}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showVerificationConfirm && user?.role === 'PROVIDER' && renderInPortal(
        <div className="modal-overlay balance-modal-overlay user-details-modal-overlay" onClick={handleCloseVerificationConfirm}>
          <div className="modal-content balance-modal user-details-modal-content verification-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('userDetails.verification.title')}</h3>
            </div>
            <div className="modal-body">
              <p className="verification-confirm-text">
                {user.verified
                  ? `${t('userDetails.verification.unverifyAction')}?`
                  : `${t('userDetails.verification.verifyAction')}?`}
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCloseVerificationConfirm}
                disabled={updatingVerification}
              >
                {t('common.cancel')}
              </button>
              <button
                className={`btn ${user.verified ? 'btn-danger' : 'btn-primary'}`}
                onClick={async () => {
                  const updated = await handleToggleVerification();
                  if (updated) setShowVerificationConfirm(false);
                }}
                disabled={updatingVerification}
              >
                {updatingVerification
                  ? t('common.loading')
                  : user.verified
                    ? t('userDetails.verification.unverifyAction')
                    : t('userDetails.verification.verifyAction')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && renderInPortal(
        <div className="modal-overlay user-edit-overlay user-details-modal-overlay" onClick={handleCloseEditModal}>
          <div className="modal-content user-edit-modal user-details-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="user-edit-modal-title-row">
                <span className="user-edit-modal-icon" aria-hidden="true">
                  <Icon name="edit-user" size={18} />
                </span>
                <h3>{t('userDetails.modal.editUserTitle')}</h3>
              </div>
            </div>
            <div className="modal-body">
              <div className="edit-user-form">
                <div className="form-group full-width">
                  <label className="form-label">
                    {t('userDetails.labels.fullName')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={editFields.fullName}
                    onChange={(e) => setEditFields({ ...editFields, fullName: e.target.value })}
                    placeholder={t('userDetails.modal.fullNamePlaceholder')}
                  />
                </div>

                <div className="form-group full-width">
                  <label className="form-label">
                    {t('userDetails.labels.email')}
                  </label>
                  <input
                    type="email"
                    className="form-input"
                    value={editFields.email}
                    onChange={(e) => setEditFields({ ...editFields, email: e.target.value })}
                    placeholder={t('userDetails.modal.emailPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('userDetails.labels.gender')}
                  </label>
                  <CustomSelect
                    value={editFields.gender}
                    onChange={(val) => setEditFields({ ...editFields, gender: val })}
                    placeholder={t('userDetails.modal.selectGender')}
                    options={[
                      { value: 'MALE', label: t('userDetails.modal.male') },
                      { value: 'FEMALE', label: t('userDetails.modal.female') }
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('userDetails.labels.language')}
                  </label>
                  <CustomSelect
                    value={editFields.preferredLanguage}
                    onChange={(val) => setEditFields({ ...editFields, preferredLanguage: val })}
                    placeholder={t('userDetails.modal.selectLanguage')}
                    options={[
                      { value: 'ro', label: 'Română' },
                      { value: 'ru', label: 'Русский' },
                      { value: 'en', label: 'English' }
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('userDetails.modal.plan')}
                  </label>
                  <CustomSelect
                    value={editFields.plan}
                    onChange={(val) => setEditFields({ ...editFields, plan: val })}
                    placeholder={t('userDetails.modal.selectPlan')}
                    options={[
                      { value: 'BASIC', label: planLabels?.BASIC || 'BASIC' },
                      { value: 'PRO', label: planLabels?.PRO || 'PRO' }
                    ]}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">
                    {t('userDetails.labels.referralCode')}
                  </label>
                  <input
                    type="text"
                    className="form-input"
                    value={editFields.referralCode}
                    onChange={(e) => setEditFields({ ...editFields, referralCode: e.target.value })}
                    placeholder={t('userDetails.modal.referralCodePlaceholder')}
                  />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn btn-secondary"
                onClick={handleCloseEditModal}
                disabled={updating}
              >
                {t('userDetails.modal.cancel')}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveUserEdit}
                disabled={updating || !editFields.fullName.trim()}
              >
                {updating ? t('common.loading') : t('userDetails.modal.save')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Balance Change Modal */}
      {showBalanceModal && renderInPortal(
        <div className="modal-overlay balance-modal-overlay user-details-modal-overlay" onClick={handleCloseBalanceModal}>
          <div className="modal-content balance-modal user-details-modal-content" onClick={(e) => e.stopPropagation()}>
            {!showBalanceConfirm ? (
              <>
                <div className="modal-header">
                  <div className="user-edit-modal-title-row">
                    <div className="user-edit-modal-icon">
                      <Icon name="credit-card" size={18} />
                    </div>
                    <h3>{t('userDetails.balance.title')}</h3>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="balance-form">
                    <div className="form-group">
                      <label className="form-label">
                        {t('userDetails.balance.currentBalance')}
                      </label>
                      <div className="current-balance-display mono">
                        {user.balance?.toFixed(2) || '0.00'} MDL
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('userDetails.balance.actionType')}
                      </label>
                      <div className="balance-type-selector">
                        <button
                          type="button"
                          className={`type-btn add ${balanceType === 'ADD' ? 'active' : ''}`}
                          onClick={() => setBalanceType('ADD')}
                        >
                          <Icon name="plus" size={14} /> {t('userDetails.balance.add')}
                        </button>
                        <button
                          type="button"
                          className={`type-btn subtract ${balanceType === 'SUBTRACT' ? 'active' : ''}`}
                          onClick={() => setBalanceType('SUBTRACT')}
                        >
                          <Icon name="minus" size={14} /> {t('userDetails.balance.subtract')}
                        </button>
                      </div>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('userDetails.balance.amount')} (MDL)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="1"
                        className={`form-input ${balanceErrors.amount ? 'error' : ''}`}
                        value={balanceAmount}
                        onChange={(e) => setBalanceAmount(e.target.value)}
                        placeholder="0.00"
                      />
                      {balanceErrors.amount && <span className="field-error">{balanceErrors.amount}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        {t('userDetails.balance.reason')}
                      </label>
                      <textarea
                        className={`form-input reason-textarea ${balanceErrors.reason ? 'error' : ''}`}
                        value={balanceReason}
                        onChange={(e) => setBalanceReason(e.target.value)}
                        placeholder={t('userDetails.balance.reasonPlaceholder')}
                      />
                      {balanceErrors.reason && <span className="field-error">{balanceErrors.reason}</span>}
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={handleCloseBalanceModal}
                    disabled={changingBalance}
                  >
                    {t('userDetails.modal.cancel')}
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleBalanceSubmit}
                    disabled={changingBalance}
                  >
                    {t('common.next')} <Icon name="arrow-right" size={14} />
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="modal-header">
                  <div className="user-edit-modal-title-row">
                    <div className="user-edit-modal-icon">
                      <Icon name="check" size={18} />
                    </div>
                    <h3>{t('userDetails.balance.confirmTitle')}</h3>
                  </div>
                </div>
                <div className="modal-body">
                  <div className="balance-confirmation-details">
                    <div className="conf-row">
                      <span className="conf-label">{t('userDetails.balance.opType')}:</span>
                      <span className={`conf-value ${balanceType === 'ADD' ? 'text-success' : 'text-danger'}`}>
                        {balanceType === 'ADD'
                          ? t('userDetails.balance.adding')
                          : t('userDetails.balance.subtracting')}
                      </span>
                    </div>
                    <div className="conf-row">
                      <span className="conf-label">{t('userDetails.balance.amount')}:</span>
                      <span className="conf-value mono bold">{parseFloat(balanceAmount).toFixed(2)} MDL</span>
                    </div>
                    <div className="conf-row">
                      <span className="conf-label">{t('userDetails.balance.reason')}:</span>
                      <span className="conf-value">{balanceReason}</span>
                    </div>
                    <div className="balance-preview-card">
                      <div className="preview-item">
                        <span>{t('userDetails.balance.current')}</span>
                        <span>{user.balance?.toFixed(2) || '0.00'} MDL</span>
                      </div>
                      <div className="preview-separator"><Icon name="arrow-right" size={14} /></div>
                      <div className="preview-item">
                        <span>{t('userDetails.balance.resulting')}</span>
                        <span className="bold">
                          {(balanceType === 'ADD'
                            ? (user.balance || 0) + parseFloat(balanceAmount)
                            : (user.balance || 0) - parseFloat(balanceAmount)).toFixed(2)} MDL
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-actions">
                  <button
                    className="btn btn-secondary"
                    onClick={() => setShowBalanceConfirm(false)}
                    disabled={changingBalance}
                  >
                    {t('userDetails.modal.cancel')}
                  </button>
                  <button
                    className={`btn ${balanceType === 'SUBTRACT' ? 'btn-danger' : 'btn-primary'}`}
                    onClick={handleConfirmBalanceChange}
                    disabled={changingBalance}
                  >
                    {changingBalance ? (
                      <>
                        <span className="strict-loader" style={{ width: '18px', height: '18px', borderWidth: '2px' }}></span>
                        {t('common.loading')}
                      </>
                    ) : (
                      <>
                        <Icon name="check" size={16} /> {t('userDetails.balance.confirm')}
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* DASHBOARD LAYOUT */}
      <div className="user-profile-dashboard">
        {/* LEFT SIDEBAR */}
        <aside className="profile-sidebar glass-panel">
          <div className="sidebar-header">
            <div className={`sidebar-avatar-wrap ${avatarStatusClass} ${user.avatar ? 'has-avatar' : 'no-avatar'}`}>
              {user.avatar ? (
                <button
                  type="button"
                  className="sidebar-avatar-button"
                  onClick={() => openLightbox([{ url: user.avatar, name: user.fullName || t('imageViewer.avatar') }], 0)}
                  title={t('imageViewer.openAvatar')}
                  aria-label={t('imageViewer.openAvatar')}
                >
                  <DeferredProfileAvatar src={user.avatar} initials={user.fullName} size={80} />
                  <span className="sidebar-avatar-zoom" aria-hidden="true">
                    <Icon name="eye" size={18} />
                  </span>
                </button>
              ) : (
                <div className="sidebar-avatar-placeholder">
                  {user.fullName ? user.fullName.charAt(0).toUpperCase() : <Icon name="user" size={36} />}
                </div>
              )}
            </div>
            <div className="sidebar-name-row">
              <h2 className="sidebar-name">{user.fullName || '—'}</h2>
              {user.role === 'PROVIDER' && Boolean(user.verified) && (
                <img
                  src={verifiedProviderBadge}
                  alt={t('userDetails.verification.verified')}
                  className="sidebar-verified-image"
                />
              )}
            </div>
            <button type="button" className="btn btn-edit-user" onClick={handleOpenEditModal} title={t('userDetails.editUser')}>
              <Icon name="edit-user" size={14} />
              {t('userDetails.edit')}
            </button>
          </div>

          <div className="sidebar-quick-stats">
            <div className="stat-item">
              <span className="stat-label">{t('userDetails.labels.role')}:</span>
              <span className="stat-value">{roleLabels?.[user.role] || user.role}</span>
            </div>
            {user.role === 'PROVIDER' && (
              <div className="stat-item balance-item">
                <span className="stat-label">{t('userDetails.labels.balance')}:</span>
                <div className="balance-row">
                  <span
                    className={`stat-value mono${user.balance != null && user.balance < 0 ? ' balance-amount-negative' : ''}`}
                  >
                    {user.balance != null ? `${user.balance.toFixed(2)} MDL` : '0.00 MDL'}
                  </span>
                  <button type="button" className="btn-edit-balance hover-trigger" onClick={handleOpenBalanceModal} title={t('userDetails.balance.edit')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#10b981', padding: '4px' }}>
                    <Icon name="edit-user" size={14} />
                  </button>
                </div>
              </div>
            )}
            {user.role === 'CUSTOMER' && (
              <div className="stat-item marketplace-balance-item">
                <span className="stat-label marketplace-balance-label">
                  <Icon name="shopping-cart" size={13} />
                  {t('userDetails.labels.marketplaceBalance')}:
                </span>
                <div className="balance-row">
                  <span className="stat-value mono marketplace-balance-value">
                    {`${Number(user.marketplaceBalance ?? 0).toFixed(2)} MDL`}
                  </span>
                </div>
              </div>
            )}
            {user.role === 'PROVIDER' && user.balance != null && user.balance < 0 && debtDurationLabel && (
              <div className="stat-item debt-status-item">
                <span className="stat-label">{t('userDetails.labels.inDebt')}:</span>
                <span className="stat-value debt-status-value">{debtDurationLabel}</span>
              </div>
            )}
          </div>

          <div className="sidebar-nav" role="navigation">
            {[
              { id: 'profile', icon: 'user', label: t('userDetails.tabs.profile') },
              { id: 'reviews', icon: 'star', label: t('userDetails.tabs.reviews', { count: reviewsTotalCount }) },
              ...(user.role === 'PROVIDER' ? [{ id: 'orders', icon: 'orders', label: t('userDetails.tabs.orders') }] : []),
              ...(user.role === 'CUSTOMER' ? [{ id: 'orders', icon: 'orders', label: t('userDetails.tabs.clientOrders', { count: ordersPagination.totalElements }) }] : []),
              { id: 'interaction', icon: 'message-square', label: interactionTabLabel }
            ].map(tab => (
              <button key={tab.id} type="button" className={`nav-tab-side ${activeTab === tab.id ? 'active' : ''}`} onClick={() => setActiveTab(tab.id)}>
                {activeTab === tab.id && <div className="nav-side-active-bg" />}
                <span className="nav-tab-side-content"><Icon name={tab.icon} size={16} /> {tab.label}</span>
              </button>
            ))}
          </div>
        </aside>

        {/* MAIN CONTENT AREA */}
        <main className="profile-dashboard-main">
          {/* PROFILE TAB (Bento Grid) */}
          {activeTab === 'profile' && (() => {
            const cardDefs = {
              contact: {
                icon: 'phone',
                title: t('userDetails.contact'),
                size: 'bento-small',
                content: (
                  <>
                    <InfoRow label={t('userDetails.labels.phone')} value={user.defaultPhoneNumber} />
                    <InfoRow label={t('userDetails.labels.email')} value={user.email} />

                    {/* Вывод адреса в едином стиле */}
                    <InfoRow
                      label={t('userDetails.labels.address')}
                      value={
                        user.address
                          ? [
                              user.address.region?.name,
                              user.address.location?.name,
                              user.address.street,
                              user.address.house ? `${t('common.houseShort')} ${user.address.house}` : null,
                              user.address.apartment ? `${t('common.apartmentShort')} ${user.address.apartment}` : null
                            ].filter(Boolean).join(', ')
                          : t('users.labels.notProvided')
                      }
                    />
                  </>
                )
              },
              personal: {
                icon: 'user', title: t('userDetails.personal'), size: 'bento-small',
                content: (<><InfoRow label={t('userDetails.labels.dateOfBirth')} value={user.dateOfBirth ? new Date(user.dateOfBirth).toLocaleDateString(locale) : t('users.labels.notProvided')} /><InfoRow label={t('userDetails.labels.gender')} value={user.gender || t('users.labels.notProvided')} /><InfoRow label={t('userDetails.labels.language')} value={user.preferredLanguage || t('users.labels.notProvided')} /></>)
              },
              business: {
                icon: 'briefcase', title: t('userDetails.business'), size: 'bento-small',
                content: (<><InfoRow label={t('userDetails.labels.company')} value={user.companyName} /><InfoRow label={t('userDetails.labels.companyRating')} value={user.companyRating} /><InfoRow label={t('userDetails.labels.verified')} value={user.verified ? t('common.yes') : t('common.no')} /><InfoRow label={t('userDetails.labels.referralCode')} value={user.referralCode} highlight /><InfoRow label={t('userDetails.labels.referrals')} value={user.referralUsersCount || 0} /><InfoRow label={t('userDetails.labels.referralsPeriod')} value={user.referralUsersCountPeriod || 0} /></>)
              },
              system: {
                icon: 'settings', title: t('userDetails.system'), size: 'bento-small',
                content: (
                  <>
                    <div className="info-row">
                      <span className="info-label">{t('userDetails.labels.userId')}</span>
                      <span className="info-value mono-token">
                        {user.id}
                        <button type="button" className="btn btn-copy hover-trigger" onClick={() => copyToClipboard(user.id)} title={t('userDetails.messages.copied')}>
                          <Icon name="copy" size={14} />
                        </button>
                      </span>
                    </div>
                    <InfoRow label={t('userDetails.labels.created')} value={new Date(user.createDate).toLocaleString(locale)} />
                    <InfoRow label={t('userDetails.labels.updated')} value={new Date(user.updateDate).toLocaleString(locale)} />
                    <InfoRow label={t('userDetails.modal.plan')} value={planLabels?.[user.plan] || user.plan || 'BASIC'} />
                    {(() => {
                      const consentState = user.marketingConsent
                        ? 'agreed'
                        : user.marketingConsentAnswered
                          ? 'declined'
                          : 'no-answer';
                      const consentLabel = {
                        agreed: t('userDetails.labels.marketingAgreed'),
                        declined: t('userDetails.labels.marketingDeclined'),
                        'no-answer': t('userDetails.labels.marketingNoAnswer'),
                      }[consentState];
                      return (
                        <div className="info-row">
                          <span className="info-label">{t('userDetails.labels.marketingConsent')}</span>
                          <span className="info-value">
                            <span className={`marketing-consent-pill is-${consentState}`}>
                              <Icon name={consentState === 'agreed' ? 'check' : consentState === 'declined' ? 'x' : 'clock'} size={12} />
                              {consentLabel}
                            </span>
                          </span>
                        </div>
                      );
                    })()}
                    {user.marketingConsentAt && (
                      <InfoRow
                        label={t('userDetails.labels.marketingConsentAt')}
                        value={new Date(user.marketingConsentAt).toLocaleString(locale)}
                      />
                    )}
                    {user.debtStartedAt && (
                      <InfoRow
                        label={t('userDetails.labels.debtStartedAt')}
                        value={new Date(user.debtStartedAt).toLocaleString(locale)}
                      />
                    )}
                  </>
                )
              },
              // PROVIDER SPECIFIC CARDS
              portfolio: {
                icon: 'briefcase', title: t('userDetails.company.title'), size: 'bento-small',
                content: (
                  <>
                    {companyData ? (
                      <>
                        {companyData.companyName && <InfoRow label={t('userDetails.company.name')} value={companyData.companyName} />}
                        {companyData.companyRating != null && <InfoRow label={t('userDetails.company.rating')} value={companyData.companyRating.toFixed(2)} />}
                        {companyData.languages && companyData.languages.length > 0 && <InfoRow label={t('userDetails.company.languages')} value={companyData.languages.join(', ')} />}
                        {companyData.openToUrgent != null && <InfoRow label={t('userDetails.company.openToUrgent')} value={companyData.openToUrgent ? t('common.yes') : t('common.no')} />}
                        {companyData.minimumCallOutPrice != null && <InfoRow label={t('userDetails.company.minimumCallOutPrice')} value={`${companyData.minimumCallOutPrice} MDL`} />}
                      </>
                    ) : companyLoading ? (
                      <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                    ) : t('common.notProvided')}
                  </>
                )
              },
              work: {
                icon: 'clock', title: t('userDetails.company.workInfo'), size: 'bento-small',
                content: (
                  <>
                    {companyData ? (
                      <>
                        {companyData.workingMethod?.teamSize != null && <InfoRow label={t('userDetails.company.teamSize')} value={companyData.workingMethod.teamSize} />}
                        {companyData.workingMethod?.team != null && <InfoRow label={t('userDetails.company.team')} value={companyData.workingMethod.team ? t('common.yes') : t('common.no')} />}
                        {companyData.schedule && (
                          <div className="schedule-display-wrapper">
                            <span className="info-label">{t('userDetails.company.schedule')}</span>
                            <ScheduleDisplay schedule={companyData.schedule} locale={locale} t={t} />
                          </div>
                        )}
                      </>
                    ) : companyLoading ? (
                      <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                    ) : t('common.notProvided')}
                  </>
                )
              },
              about: {
                icon: 'company', title: t('userDetails.company.about'), size: 'bento-small',
                content: (
                  <>
                    {companyData ? (
                      <>
                        {companyData.description && <div className="info-row"><span className="info-label">{t('userDetails.company.description')}</span><span className="info-value">{companyData.description}</span></div>}
                        {companyData.bio && <div className="info-row"><span className="info-label">{t('userDetails.company.bio')}</span><span className="info-value">{companyData.bio}</span></div>}
                      </>
                    ) : companyLoading ? (
                      <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                    ) : t('common.notProvided')}
                  </>
                )
              },
              categories: {
                icon: 'tag', title: `${t('userDetails.company.categories')} (${companyData?.categories?.length || 0})`, size: 'bento-small',
                content: (
                  companyData ? (
                    <div className="categories-list-simple">
                      {companyData.categories?.map((category, index) => <div key={category.id || index} className="category-item-simple">{category.title || t('userDetails.company.categoryFallback', { index: index + 1 })}</div>)}
                    </div>
                  ) : companyLoading ? (
                    <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                  ) : t('common.notProvided')
                )
              },
              regions: {
                icon: 'map', title: `${t('userDetails.company.regions')} (${companyData?.regions?.length || 0})`, size: 'bento-small',
                content: (
                  companyData ? (
                    <div className="regions-inline">
                      {companyData.regions?.map((region, index) => <span key={region.code || index} className="region-inline">{region.name}{index < companyData.regions.length - 1 && <span className="region-separator">,</span>}</span>)}
                    </div>
                  ) : companyLoading ? (
                    <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                  ) : t('common.notProvided')
                )
              },
              services: {
                icon: 'layers', title: `${t('userDetails.company.services')} (${companyData?.services?.length || 0})`, size: 'bento-small',
                content: (
                  companyData ? (
                    <>
                      {companyData.services?.map((service, index) => <InfoRow key={service.id || index} label={service.name || t('userDetails.company.serviceFallback', { index: index + 1 })} value={service.price != null ? `${service.price.toFixed(2)} MDL` : '—'} />)}
                    </>
                  ) : companyLoading ? (
                    <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                  ) : t('common.notProvided')
                )
              },
              documents: {
                icon: 'file-text', title: `${t('userDetails.company.documents')} (${companyData?.documents?.length || 0})`, size: 'bento-small',
                content: (
                  companyData ? (
                    <div className="compact-list">
                      <div className="documents-compact">
                        {companyData.documents?.map((doc, index) => (
                          <div key={doc.fileKey || index} className="document-compact">
                            {doc.fileUrl ? (
                              <a href={doc.fileUrl} target="_blank" rel="noopener noreferrer" className="document-link-small">
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icon name="file-text" size={14} /> {doc.fileName || doc.fileKey || t('userDetails.company.documentFallback', { index: index + 1 })}</span>
                              </a>
                            ) : (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}><Icon name="file-text" size={14} /> {doc.fileName || doc.fileKey || t('userDetails.company.documentFallback', { index: index + 1 })}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : companyLoading ? (
                    <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading')}</div></div>
                  ) : t('common.notProvided')
                )
              },
              photos: {
                icon: 'image', title: `${t('userDetails.company.photos')} (${companyData?.photos?.length || 0})`, size: 'bento-small',
                content: (
                  companyData ? (() => {
                    const allPhotos = companyData.photos || [];
                    // По умолчанию показываем только первые PHOTOS_PREVIEW_LIMIT, остальное — по кнопке.
                    const visiblePhotos = photosExpanded ? allPhotos : allPhotos.slice(0, PHOTOS_PREVIEW_LIMIT);
                    const hiddenCount = allPhotos.length - visiblePhotos.length;
                    const lightboxImages = allPhotos
                      .map((photo, index) => ({
                        url: photo.fileUrl,
                        name: photo.fileName || t('userDetails.company.photoFallback', { index: index + 1 })
                      }))
                      .filter((image) => image.url);

                    return (
                      <>
                        <div className="photos-grid-styled">
                          {visiblePhotos.map((photo, index) => (
                            <div key={photo.fileKey || index} className="photo-item-styled">
                              {photo.fileUrl ? (
                                <button
                                  type="button"
                                  className="photo-link"
                                  onClick={() => openLightbox(
                                    lightboxImages,
                                    lightboxImages.findIndex((image) => image.url === photo.fileUrl)
                                  )}
                                  title={photo.fileName || t('userDetails.company.photoFallback', { index: index + 1 })}
                                >
                                  <img
                                    src={photo.fileUrl}
                                    alt={photo.fileName || t('userDetails.company.photoFallback', { index: index + 1 })}
                                    className="photo-image"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                  <div className="photo-overlay"><Icon name="eye" size={24} className="photo-icon" /></div>
                                </button>
                              ) : (
                                <div className="photo-placeholder-styled">
                                  <span className="photo-placeholder-icon"><Icon name="image" size={24} /></span>
                                  <span className="photo-placeholder-text">
                                    {t('userDetails.company.photoFallback', { index: index + 1 })}
                                  </span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                        {allPhotos.length > PHOTOS_PREVIEW_LIMIT && (
                          <button
                            type="button"
                            className="photos-toggle-btn"
                            onClick={() => setPhotosExpanded((prev) => !prev)}
                          >
                            <Icon name={photosExpanded ? 'chevron-up' : 'chevron-down'} size={14} />
                            {photosExpanded
                              ? t('imageViewer.showLess')
                              : t('imageViewer.showMore', { n: hiddenCount })}
                          </button>
                        )}
                      </>
                    );
                  })() : companyLoading ? (
                    <div className="loading-state"><div className="strict-loader"></div><div>{t('userDetails.company.loading', { defaultValue: 'Загрузка данных компании...' })}</div></div>
                  ) : t('common.notProvided')
                )
              }
            };

            const availableCards = user.role === 'PROVIDER' ? [...DEFAULT_CARD_ORDER, ...PROVIDER_CARD_IDS] : DEFAULT_CARD_ORDER;

            return (
              <div className="bento-grid-layout">
                {cardOrder.filter(id => availableCards.includes(id)).map(cardId => {
                  const card = cardDefs[cardId];
                  if (!card) return null;
                  const isPinned = pinnedCards.includes(cardId);
                  const isDragging = draggedCard === cardId;
                  const isDragOver = dragOverCard === cardId;
                  return (
                    <div
                      key={cardId}
                      data-cardid={cardId}
                      className={`bento-card ${card.size} ${isDragging ? 'dragging' : ''} ${isDragOver ? 'drag-over' : ''} ${isPinned ? 'pinned' : ''}`}
                      onPointerDown={(e) => handlePointerDown(e, cardId)}
                      style={{ touchAction: 'none' }}
                    >
                      <div className="card-header">
                        <h3 className="card-title">
                          <span className="card-icon-circle" aria-hidden="true"><Icon name={card.icon} size={13} /></span>
                          {card.title}
                        </h3>
                        <button type="button" className={`btn-pin-card ${isPinned ? 'pinned' : ''}`} onClick={(e) => { e.stopPropagation(); togglePinCard(cardId); }} title={isPinned ? 'Открепить' : 'Закрепить'}>
                          <Icon name={isPinned ? 'lock' : 'unlock'} size={14} />
                        </button>
                      </div>
                      <div className="card-content">{card.content}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* REVIEWS TAB */}
          {activeTab === 'reviews' && (
            <div className="tab-content">
              <div className="reviews-container">
                <div className="reviews-header">
                  <h2>{t('userDetails.reviews.title')}</h2>
                  <button type="button" className="btn btn-refresh-reviews" onClick={() => loadReviews(reviewsPage, reviewsPageSize)} disabled={reviewsLoading}>
                    <Icon name="refresh" size={16} className={reviewsLoading ? 'spinning' : ''} />
                    {reviewsLoading ? t('common.loading') : t('userDetails.reviews.refresh')}
                  </button>
                </div>

                {renderReviewsPager('top')}

                {reviewsLoading ? (
                  <div className="loading-state">
                    <div className="strict-loader"></div>
                    {t('userDetails.reviews.loading')}
                  </div>
                ) : reviews.length > 0 ? (
                  <div className="reviews-list">
                    {reviews.map((review) => {
                      const reviewerName = review.reviewerFullName || t('nav.userFallback');
                      const canRemove = review.canRemove || canDeleteReviews;

                      return (
                        <div key={review.id} className="review-card">
                          <div className="review-header">
                            <div className="reviewer-info">
                              {review.reviewerAvatar ? (
                                <img
                                  src={review.reviewerAvatar}
                                  alt="avatar"
                                  className="reviewer-avatar"
                                  loading="lazy"
                                  decoding="async"
                                />
                              ) : (
                                <div className="reviewer-avatar-placeholder">
                                  {reviewerName?.charAt(0) || '?'}
                                </div>
                              )}
                              <div>
                                <div className="reviewer-name">{reviewerName}</div>
                                <div className="review-meta">
                                  <span className="review-rating">
                                    {review.rating != null ? (
                                      <>
                                        {renderStars(review.rating)}{' '}
                                        {Number(review.rating).toFixed(1)}
                                      </>
                                    ) : (
                                      t('userDetails.reviews.noRating')
                                    )}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="review-date">
                              {new Date(review.createdAt).toLocaleDateString(locale)}
                            </div>
                          </div>
                          <div className="review-content">
                            <p>{review.comment?.trim() || t('userDetails.reviews.noComment')}</p>
                            {(review.reviewerId || canRemove) && (
                              <div className="review-actions">
                                {review.reviewerId && (
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => handleOpenReviewer(review.reviewerId)}
                                    title={t('userDetails.reviews.viewProfile')}
                                    aria-label={t('userDetails.reviews.viewProfile')}
                                  >
                                    <Icon name="user" size={20} />
                                  </button>
                                )}
                                {canRemove && (
                                  <button
                                    type="button"
                                    className="btn-icon danger"
                                    onClick={() => handleDeleteReview(review.id)}
                                    disabled={deletingReviewId === review.id}
                                    title={t('userDetails.reviews.delete')}
                                    aria-label={t('userDetails.reviews.delete')}
                                  >
                                    <Icon name="trash-2" size={20} />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <AnimatedEmptyState
                    iconName="message-circle"
                    title={t('userDetails.reviews.emptyTitle')}
                    subtitle={t('userDetails.reviews.emptyText')}
                    className="reviews-empty-state"
                  />
                )}

                {!reviewsLoading && reviews.length > 0 && renderReviewsPager('bottom')}
              </div>
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (user.role === 'PROVIDER' || user.role === 'CUSTOMER') && (() => {
            const isClientOrders = user.role === 'CUSTOMER';
            return (
            <div className="tab-content">
              <div className="orders-container">
                <div className="orders-header-section">
                  <h2>{isClientOrders ? t('userDetails.orders.clientTitle') : t('userDetails.orders.title')}</h2>
                  <div className="orders-sort-controls">
                    <label className="sort-label">
                      {t('userDetails.orders.sortBy')}:
                    </label>
                    <select
                      className="sort-select"
                      value={ordersSort}
                      onChange={(e) => {
                        setOrdersSort(e.target.value);
                        setOrdersPage(0);
                      }}
                    >
                      <option value="createdAt">{t('userDetails.orders.sortByCreated')}</option>
                      <option value="updatedAt">{t('userDetails.orders.sortByUpdated')}</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-sort-direction"
                      onClick={() => {
                        setOrdersDirection(ordersDirection === 'ASC' ? 'DESC' : 'ASC');
                        setOrdersPage(0);
                      }}
                      title={ordersDirection === 'ASC' ? t('userDetails.orders.sortDesc') : t('userDetails.orders.sortAsc')}
                    >
                      {ordersDirection === 'ASC' ? <Icon name="chevron-up" size={16} /> : <Icon name="chevron-down" size={16} />}
                    </button>
                  </div>
                </div>

                {ordersLoading ? (
                  <div className="loading-state">
                    <div className="strict-loader"></div>
                    <div>{t('userDetails.orders.loading', { defaultValue: 'Загрузка заказов...' })}</div>
                  </div>
                ) : purchasedOrders.length > 0 ? (
                  <>
                    <div className="orders-table-wrapper">
                      <table className="orders-table-simple">
                        <thead>
                          <tr>
                            <th>{t('userDetails.orders.table.title')}</th>
                            <th>{t('userDetails.orders.table.status')}</th>
                            {!isClientOrders && <th>{t('userDetails.orders.table.accepted')}</th>}
                            <th>{t('userDetails.orders.table.createdAt')}</th>
                            <th>{t('userDetails.orders.table.updatedAt')}</th>
                            <th>{t('userDetails.orders.table.actions')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {purchasedOrders.map((order) => {
                            const orderId = order.orderId ?? order.id;
                            return (
                            <tr key={orderId}>
                              <td className="order-title-cell">
                                <span title={order.title}>{order.title || '—'}</span>
                              </td>
                              <td className="order-status-cell">
                                <StatusBadge type="order" status={order.status} />
                              </td>
                              {!isClientOrders && (
                                <td className="order-accepted-cell">
                                  <span className={order.accepted ? 'accepted-yes' : 'accepted-no'}>
                                    {order.accepted ? <Icon name="check" size={16} /> : <Icon name="x" size={16} />}
                                  </span>
                                </td>
                              )}
                              <td className="order-date-cell">
                                {order.createdAt ? new Date(order.createdAt).toLocaleString(locale) : '—'}
                              </td>
                              <td className="order-date-cell">
                                {order.updatedAt ? new Date(order.updatedAt).toLocaleString(locale) : '—'}
                              </td>
                              <td className="order-actions-cell">
                                <Link
                                  to={`/orders/${orderId}`}
                                  state={pushDetailNavState(location, { fromUser: location.pathname })}
                                  className="btn btn-link-sm"
                                >
                                  {t('userDetails.orders.view')}
                                </Link>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {ordersPagination.totalPages > 1 && (
                      <div className="orders-pagination">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setOrdersPage(prev => Math.max(0, prev - 1))}
                          disabled={ordersPage === 0 || ordersLoading}
                        >
                          {t('common.pagination.previous')}
                        </button>
                        <span className="pagination-info">
                          {t('common.pagination.pageOf', {
                            page: ordersPage + 1,
                            total: ordersPagination.totalPages,
                          })}{' '}
                          ({ordersPagination.totalElements} {t('common.pagination.items')})
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setOrdersPage(prev => Math.min(ordersPagination.totalPages - 1, prev + 1))}
                          disabled={ordersPage >= ordersPagination.totalPages - 1 || ordersLoading}
                        >
                          {t('common.pagination.next')}
                        </button>
                        <select
                          className="page-size-select"
                          value={ordersPageSize}
                          onChange={(e) => {
                            setOrdersPageSize(parseInt(e.target.value));
                            setOrdersPage(0);
                          }}
                        >
                          <option value="10">10</option>
                          <option value="20">20</option>
                          <option value="50">50</option>
                        </select>
                      </div>
                    )}
                  </>
                ) : (
                  <AnimatedEmptyState
                    iconName="orders"
                    title={isClientOrders ? t('userDetails.orders.clientEmptyTitle') : t('userDetails.orders.emptyTitle')}
                    subtitle={isClientOrders ? t('userDetails.orders.clientEmptyText') : t('userDetails.orders.emptyText')}
                  />
                )}
              </div>
            </div>
            );
          })()}

          {/* INTERACTION TAB */}
          {activeTab === 'interaction' && (
            <div className="tab-content">
              <div className="interaction-container">
                <div className="interaction-section">
                  <div className="interaction-section-head">
                    <span className="interaction-section-icon">
                      <Icon name="shield" size={18} />
                    </span>
                    <div className="interaction-section-titles">
                      <h2>{t('userDetails.interaction.statusTitle')}</h2>
                      <p className="interaction-section-sub">
                        {t('userDetails.interaction.statusSub', { defaultValue: 'Управляйте доступом пользователя к платформе' })}
                      </p>
                    </div>
                  </div>
                  <div className="status-inline">
                    {userStatuses.map((status) => (
                      <button
                        type="button"
                        key={status.value}
                        className={`btn btn-status ${user.status === status.value ? 'active' : ''}`}
                        onClick={() => handleStatusChange(status.value)}
                        disabled={user.status === status.value || updating}
                      >
                        <span className={`dot dot-${status.color}`}></span>
                        {status.label}
                      </button>
                    ))}
                  </div>
                </div>

                {user.role === 'PROVIDER' && (
                  <div className={`verification-banner ${user.verified ? 'is-verified' : 'is-unverified'}`}>
                    <div className="verification-banner-icon">
                      <Icon name={user.verified ? 'check-circle' : 'alert-triangle'} size={24} />
                    </div>
                    <div className="verification-banner-body">
                      <span className="verification-banner-title">
                        {t('userDetails.verification.title')}
                      </span>
                      <span className="verification-banner-status">
                        {user.verified
                          ? t('userDetails.verification.verified')
                          : t('userDetails.verification.notVerified')}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={`btn verification-banner-btn ${user.verified ? 'btn-unverify' : 'btn-verify'}`}
                      onClick={handleOpenVerificationConfirm}
                      disabled={updatingVerification}
                    >
                      {updatingVerification
                        ? t('common.loading')
                        : user.verified
                          ? t('userDetails.verification.unverifyAction')
                          : t('userDetails.verification.verifyAction')}
                    </button>
                  </div>
                )}

                {user.role === 'PROVIDER' && (currentRole === 'ADMIN' || currentRole === 'DEV') && (
                  <div className={`interaction-section review-section ${reviewSectionOpen ? 'is-open' : 'is-collapsed'}`}>
                    <button
                      type="button"
                      className="interaction-section-head interaction-section-toggle"
                      onClick={() => setReviewSectionOpen((prev) => !prev)}
                      aria-expanded={reviewSectionOpen}
                      aria-controls="review-section-body"
                    >
                      <span className="interaction-section-icon accent-amber">
                        <Icon name="star-fill" size={18} />
                      </span>
                      <div className="interaction-section-titles">
                        <h2>{t('userDetails.interaction.review.title', { defaultValue: 'Оставить отзыв мастеру' })}</h2>
                        <p className="interaction-section-sub">
                          {t('userDetails.interaction.review.subtitle', { defaultValue: 'Опубликуйте официальный отзыв от имени выбранного аккаунта' })}
                        </p>
                      </div>
                      <span className="interaction-section-chevron" aria-hidden="true">
                        <Icon name="chevron-down" size={20} />
                      </span>
                    </button>

                    {reviewSectionOpen && (
                    <div className="review-form" id="review-section-body">
                      <div className="review-rating-field">
                        <label className="review-field-label">
                          {t('userDetails.interaction.review.ratingLabel', { defaultValue: 'Оценка' })}
                        </label>
                        <div className="review-stars-input" role="radiogroup">
                          {[1, 2, 3, 4, 5].map((value) => {
                            const filled = value <= (reviewHover || reviewRating);
                            return (
                              <button
                                type="button"
                                key={value}
                                className={`review-star-btn ${filled ? 'active' : ''}`}
                                onClick={() => setReviewRating(value)}
                                onMouseEnter={() => setReviewHover(value)}
                                onMouseLeave={() => setReviewHover(0)}
                                aria-label={`${value} / 5`}
                              >
                                <Icon name={filled ? 'star-fill' : 'star'} size={26} />
                              </button>
                            );
                          })}
                          <span className="review-rating-value">{reviewRating.toFixed(1)}</span>
                        </div>
                      </div>

                      <div className="review-input-field">
                        <label className="review-field-label" htmlFor="review-reviewer-id">
                          {t('userDetails.interaction.review.reviewerLabel', { defaultValue: 'ID аккаунта, от которого оставляется отзыв' })}
                        </label>
                        <input
                          id="review-reviewer-id"
                          type="text"
                          className="review-text-input"
                          value={reviewReviewerId}
                          onChange={(e) => setReviewReviewerId(e.target.value)}
                          placeholder="00000000-0000-0000-0000-000000000000"
                          spellCheck={false}
                          autoComplete="off"
                        />
                      </div>

                      <div className="review-input-field">
                        <label className="review-field-label" htmlFor="review-comment">
                          {t('userDetails.interaction.review.commentLabel', { defaultValue: 'Комментарий' })}
                        </label>
                        <textarea
                          id="review-comment"
                          className="note-textarea"
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          placeholder={t('userDetails.interaction.review.commentPlaceholder', { defaultValue: 'Опишите качество работы мастера...' })}
                          rows={4}
                        />
                      </div>

                      <div className="note-actions">
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setReviewRating(5);
                            setReviewReviewerId('');
                            setReviewComment('');
                          }}
                          disabled={submittingReview || (!reviewReviewerId && !reviewComment && reviewRating === 5)}
                        >
                          {t('userDetails.interaction.clear')}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSubmitReview}
                          disabled={submittingReview || !reviewReviewerId.trim()}
                        >
                          {submittingReview
                            ? t('common.loading')
                            : t('userDetails.interaction.review.submit', { defaultValue: 'Опубликовать отзыв' })}
                        </button>
                      </div>
                    </div>
                    )}
                  </div>
                )}

                <div className="interaction-section">
                  <div className="interaction-section-head">
                    <span className="interaction-section-icon">
                      <Icon name="file-text" size={18} />
                    </span>
                    <div className="interaction-section-titles">
                      <h2>{t('userDetails.interaction.noteTitle')}</h2>
                      <p className="interaction-section-sub">
                        {t('userDetails.interaction.noteSub', { defaultValue: 'Внутренние заметки видны только команде' })}
                      </p>
                    </div>
                  </div>
                  <div className="note-form">
                    <textarea
                      className="note-textarea"
                      value={interactionNote}
                      onChange={(e) => setInteractionNote(e.target.value)}
                      placeholder={t('userDetails.interaction.notePlaceholder')}
                      rows={5}
                    />
                    <div className="note-actions">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setInteractionNote('')}
                        disabled={!interactionNote}
                      >
                        {t('userDetails.interaction.clear')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSaveNote}
                        disabled={!interactionNote.trim()}
                      >
                        {t('userDetails.interaction.save')}
                      </button>
                    </div>
                  </div>
                  {userCommentsLoading ? (
                    <p>{t('common.loading')}</p>
                  ) : userComments && userComments.length > 0 ? (
                    <div className="user-comments-list">
                      {userComments.map((comment) => {
                        const cid = comment.id || comment.commentId || comment.createdAt;
                        const isEditing = editingCommentId && (comment.id === editingCommentId || comment.commentId === editingCommentId);
                        const authorName =
                          comment.authorFullName ||
                          comment.authorName ||
                          comment.createdByName ||
                          comment.createdBy ||
                          '';
                        const initial = authorName ? authorName.charAt(0).toUpperCase() : '?';
                        return (
                          <div key={cid} className="user-comment-card">
                            <div className="user-comment-card-header">
                              <div className="user-comment-card-author">
                                <span className="user-comment-avatar" aria-hidden="true">
                                  {initial}
                                </span>
                                <div className="user-comment-card-meta">
                                  {authorName && (
                                    <span className="user-comment-card-name">{authorName}</span>
                                  )}
                                  {comment.authorRole && (
                                    <span className="user-comment-role">
                                      {t(`users.roles.${comment.authorRole}`, comment.authorRole)}
                                    </span>
                                  )}
                                </div>
                              </div>
                              {comment.createdAt && (
                                <time className="user-comment-card-date" dateTime={comment.createdAt}>
                                  {new Date(comment.createdAt).toLocaleString(locale)}
                                </time>
                              )}
                            </div>
                            {isEditing ? (
                              <div className="user-comment-edit">
                                <textarea
                                  className="note-textarea"
                                  rows={3}
                                  value={editingCommentText}
                                  onChange={(e) => setEditingCommentText(e.target.value)}
                                />
                                <div className="user-comment-actions">
                                  <button
                                    className="btn btn-secondary btn-xs"
                                    type="button"
                                    onClick={cancelEditComment}
                                  >
                                    {t('common.cancel')}
                                  </button>
                                  <button
                                    className="btn btn-primary btn-xs"
                                    type="button"
                                    onClick={handleUpdateComment}
                                    disabled={!editingCommentText.trim()}
                                  >
                                    {t('common.save')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="user-comment-card-body">
                                <div className="user-comment-card-text">
                                  {comment.text || comment.comment}
                                </div>
                                <div className="user-comment-card-actions">
                                  <button
                                    type="button"
                                    className="btn-icon hover-trigger"
                                    onClick={() => startEditComment(comment)}
                                    title={t('common.edit')}
                                  >
                                    <Icon name="pencil" size={20} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn-icon danger hover-trigger"
                                    onClick={() => handleDeleteComment(comment)}
                                    title={t('common.delete')}
                                  >
                                    <Icon name="trash-2" size={20} />
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}

        </main>
      </div>

      {lightbox && (
        <ImageLightbox
          images={lightbox.images}
          initialIndex={lightbox.index}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};

// Helper Component for consistent rows
const InfoRow = ({ label, value, highlight }) => (
  <div className="info-row">
    <span className="info-label">{label}</span>
    <span className={`info-value ${highlight ? 'highlight' : ''}`}>{value || '—'}</span>
  </div>
);

// Schedule Display Component
const ScheduleDisplay = ({ schedule, locale, t }) => {
  const daysOfWeek = [
    { key: 'monday', label: t('userDetails.schedule.monday') },
    { key: 'tuesday', label: t('userDetails.schedule.tuesday') },
    { key: 'wednesday', label: t('userDetails.schedule.wednesday') },
    { key: 'thursday', label: t('userDetails.schedule.thursday') },
    { key: 'friday', label: t('userDetails.schedule.friday') },
    { key: 'saturday', label: t('userDetails.schedule.saturday') },
    { key: 'sunday', label: t('userDetails.schedule.sunday') }
  ];

  const formatTime = (timeValue) => {
    if (!timeValue) return '';
    try {
      if (typeof timeValue === 'string') {
        const time = timeValue.split(':');
        return `${time[0]}:${time[1]}`;
      }
      if (typeof timeValue === 'object' && timeValue.hour != null) {
        const hour = String(timeValue.hour).padStart(2, '0');
        const minute = String(timeValue.minute || 0).padStart(2, '0');
        return `${hour}:${minute}`;
      }
      return String(timeValue);
    } catch (e) {
      console.error('Error formatting time:', e, timeValue);
      return String(timeValue);
    }
  };

  const renderDaySchedule = (dayKey, dayLabel) => {
    const daySchedule = schedule[dayKey];
    if (!daySchedule) return null;

    if (daySchedule.dayOff) {
      return (
        <div key={dayKey} className="schedule-day">
          <span className="schedule-day-name">{dayLabel}</span>
          <span className="schedule-day-off">{t('userDetails.schedule.dayOff')}</span>
        </div>
      );
    }

    if (daySchedule.flexible) {
      return (
        <div key={dayKey} className="schedule-day">
          <span className="schedule-day-name">{dayLabel}</span>
          <span className="schedule-flexible">{t('userDetails.schedule.flexible')}</span>
        </div>
      );
    }

    if (daySchedule.intervals && Array.isArray(daySchedule.intervals) && daySchedule.intervals.length > 0) {
      return (
        <div key={dayKey} className="schedule-day">
          <span className="schedule-day-name">{dayLabel}</span>
          <div className="schedule-intervals">
            {daySchedule.intervals.map((interval, idx) => {
              const startTime = formatTime(interval.start);
              const endTime = formatTime(interval.end);
              if (!startTime || !endTime) return null;
              return (
                <span key={idx} className="schedule-interval">
                  {startTime} - {endTime}
                </span>
              );
            })}
          </div>
        </div>
      );
    }

    return null;
  };

  const scheduleDays = daysOfWeek
    .map(({ key, label }) => renderDaySchedule(key, label))
    .filter(day => day !== null);

  if (scheduleDays.length === 0) {
    return (
      <span className="info-value" style={{ color: '#64748b', fontStyle: 'italic' }}>
        {t('userDetails.schedule.notConfigured')}
      </span>
    );
  }

  return (
    <div className="schedule-display">
      {scheduleDays}
    </div>
  );
};

export default UserDetails;